import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { rrulestr } from 'rrule';

import { useNavbar } from '../../../hooks/use-navbar';
import { useAuth } from '../../../hooks/use-auth';
import { UserJson } from '../../../types/models';
import { faroLogger } from '../../../observability/faro-logger';
import { useBlockedClients } from '../../../hooks/use-blocked-clients';
import { apiClient } from '../../../api';
import WhatsNewPopup, { useWhatsNew } from './WhatsNewPopup';

dayjs.locale('fr');

interface TodayClient {
  id: number;
  name: string;
  done: boolean;
}

function isPlannedForToday(item: {
  date?: string;
  rrule?: string | null;
  exdates?: string[] | null;
}): boolean {
  const today = dayjs().format('YYYY-MM-DD');
  if (!item.rrule) {
    return item.date === today;
  }
  try {
    const start = dayjs().startOf('day').toDate();
    const end = dayjs().endOf('day').toDate();
    const occurs = rrulestr(item.rrule).between(start, end, true);
    if (occurs.length === 0) {
      return false;
    }
    const exdates = new Set(item.exdates ?? []);
    return !exdates.has(today);
  } catch {
    return false;
  }
}

const Icon: FC<{ name: string; size?: number; stroke?: number }> = ({ name, size = 22, stroke = 1.8 }) => {
  const w = size;
  const props = {
    width: w,
    height: w,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: stroke,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
  case 'camera':
    return (
      <svg {...props}>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
    );
  case 'calendar':
    return (
      <svg {...props}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  case 'phone':
    return (
      <svg {...props}>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    );
  case 'book':
    return (
      <svg {...props}>
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    );
  case 'clock':
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    );
  case 'user':
    return (
      <svg {...props}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );
  case 'list':
    return (
      <svg {...props}>
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" />
        <line x1="3" y1="12" x2="3.01" y2="12" />
        <line x1="3" y1="18" x2="3.01" y2="18" />
      </svg>
    );
  case 'arrow-right':
    return (
      <svg {...props} strokeWidth={2.2}>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    );
  case 'help':
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  default:
    return null;
  }
};

const HomePage: FC = () => {
  const navbar = useNavbar();
  const navigate = useNavigate();
  const auth = useAuth();
  const { open: whatsNewOpen, close: closeWhatsNew } = useWhatsNew();
  const user = (auth.user || undefined) as UserJson | undefined;
  const firstName = user
    ? (user.title || '').split(' ')[0] || user.lastname || 'agent'
    : 'agent';

  useEffect(() => {
    if (!navbar) {
      return;
    }
    navbar.setTitle('Camion Net');
    navbar.setBackArrow(false);
    navbar.setRight();
    faroLogger.info('home_opened');
  }, [navbar]);

  const { enabled: blockedEnabled, data: blockedData } = useBlockedClients();
  const blockedCount = blockedData.length;
  const blockedTotal = blockedData.reduce((s, c) => s + c.totalDue, 0);

  // Planning du jour : clients planifies + statut "saisi" si interv. aujourd hui
  const [todayClients, setTodayClients] = useState<TodayClient[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    const today = dayjs().format('YYYY-MM-DD');
    apiClient.planning
      .getPlanning({ from: today, to: today })
      .then((raw) => {
        if (cancelled) {
          return;
        }
        const todays = (raw || []).filter((d: any) => isPlannedForToday(d));
        const clientMap = new Map<number, string>();
        todays.forEach((d: any) => {
          (d.clients || []).forEach((c: any) => {
            if (!clientMap.has(c.id)) {
              clientMap.set(c.id, c.name || c.display_name || `Client ${c.id}`);
            }
          });
        });
        const ids = Array.from(clientMap.keys());
        if (ids.length === 0) {
          setTodayClients([]);
          return;
        }
        Promise.all(
          ids.map((cid) =>
            apiClient.interventions
              .getInterventions({ filter: `client_id||eq||${cid}`, limit: 5000 })
              .then((r) => ({ cid, ivs: r.data || [] }))
              .catch(() => ({ cid, ivs: [] as any[] })),
          ),
        ).then((results) => {
          if (cancelled) {
            return;
          }
          const list: TodayClient[] = ids.map((cid) => {
            const r = results.find((x) => x.cid === cid);
            const done = !!r?.ivs.some(
              (iv: any) =>
                iv.timestamp && dayjs(iv.timestamp).format('YYYY-MM-DD') === today,
            );
            return { id: cid, name: clientMap.get(cid) || `Client ${cid}`, done };
          });
          // tri : non faits d abord, puis ordre alphabetique
          list.sort((a, b) => {
            if (a.done !== b.done) {
              return a.done ? 1 : -1;
            }
            return a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });
          });
          setTodayClients(list);
        });
      })
      .catch(() => {
        if (!cancelled) {
          setTodayClients([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const todayDone = todayClients?.filter((c) => c.done).length || 0;
  const todayTotal = todayClients?.length || 0;
  const todayPct = todayTotal > 0 ? Math.round((todayDone / todayTotal) * 100) : 0;

  // Décompte avant l'obligation de signature client (1er octobre 2026).
  const joursAvantLancement = Math.max(
    0,
    Math.ceil((new Date('2026-10-01T00:00:00+02:00').getTime() - Date.now()) / 86400000),
  );

  const cards = [
    { key: '/planning', title: 'Planning', desc: 'Vos clients du jour', icon: 'calendar' },
    { key: '/clients', title: 'Clients', desc: 'Liste complète des clients', icon: 'list' },
    { key: '/contact', title: 'Contact', desc: 'Joindre le bureau', icon: 'phone' },
    { key: '/livret', title: 'Livret de bord', desc: 'Consignes et infos', icon: 'book' },
    { key: '/skello', title: 'Skello', desc: 'Indiquer ma présence chez le client', icon: 'clock' },
    { key: '/me', title: 'Mon compte', desc: 'Mes infos et déconnexion', icon: 'user' },
  ];

  const onCardClick = (key: string) => {
    if (key === '/skello') {
      window.open('https://app.skello.io/users/sign_in?lang=fr#', '_blank');
      return;
    }
    navigate(key);
  };

  return (
    <div style={{ padding: '14px 14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          marginBottom: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Bonjour</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              marginTop: 2,
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 900,
                color: 'var(--text-primary)',
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
                textTransform: 'capitalize',
              }}
            >
              {firstName}
              <span style={{ color: 'var(--teal)' }}>.</span>
            </div>
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-muted)',
              marginTop: 6,
              textTransform: 'capitalize',
            }}
          >
            {dayjs().format('dddd D MMMM YYYY')}
          </div>
        </div>
      </div>

      {/* Rappel signature client (1er oct) + accès démo, dans une même tuile — rouge pour alerter */}
      <div
        style={{
          background: 'rgba(230,57,70,0.10)',
          border: '1px solid rgba(230,57,70,0.45)',
          borderLeft: '4px solid #E63946',
          borderRadius: 14,
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px' }}>
          <span aria-hidden style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>
            📅
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div
                style={{
                  fontSize: 14.5,
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  lineHeight: 1.25,
                  letterSpacing: '-0.005em',
                }}
              >
                À partir du 1<sup>er</sup> octobre
              </div>
              {joursAvantLancement > 0 && (
                <span
                  style={{
                    background: '#E63946',
                    color: '#fff',
                    borderRadius: 100,
                    padding: '2px 9px',
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: '0.04em',
                    lineHeight: 1.4,
                    flexShrink: 0,
                  }}
                >
                  J-{joursAvantLancement}
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.4 }}>
              Les bons d&rsquo;intervention en ligne devront être{' '}
              <b style={{ color: '#E63946' }}>systématiquement signés par le client</b>.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5, fontWeight: 700 }}>
              Concerne les agents hors centre de lavage de Rungis.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/demo')}
          style={{
            appearance: 'none',
            width: '100%',
            background: 'transparent',
            border: 'none',
            borderTop: '1px solid rgba(230,57,70,0.3)',
            padding: '11px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            textAlign: 'left',
            color: '#E63946',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 800,
          }}
        >
          <Icon name="help" size={16} />
          <span style={{ flex: 1, minWidth: 0 }}>Démo</span>
          <Icon name="arrow-right" size={14} />
        </button>
      </div>

      {/* Planning du jour - cliquable client par client */}
      {todayClients && todayClients.length > 0 && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 14,
            padding: '12px 14px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 10,
            }}
          >
            <button
              type="button"
              onClick={() => navigate('/planning')}
              style={{
                appearance: 'none',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                cursor: 'pointer',
                padding: 0,
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--teal)',
                }}
              >
                Aujourd&rsquo;hui · {todayTotal} client
                {todayTotal > 1 ? 's' : ''}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                {todayDone === todayTotal && todayTotal > 0 ? (
                  <span style={{ color: '#10B981', fontWeight: 700 }}>
                    ✓ Tout est saisi · Voir le planning
                  </span>
                ) : (
                  <>
                    <b style={{ color: 'var(--text-primary)', fontWeight: 800 }}>{todayDone}</b>
                    /{todayTotal} saisis · Voir le planning
                  </>
                )}
              </div>
            </button>
          </div>

          {/* Barre de progression */}
          <div
            style={{
              height: 6,
              background: 'var(--bg-card)',
              borderRadius: 100,
              overflow: 'hidden',
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: `${todayPct}%`,
                height: '100%',
                background:
                  todayDone === todayTotal
                    ? '#10B981'
                    : 'linear-gradient(90deg, var(--teal), #10B981)',
                borderRadius: 100,
                transition: 'width 0.4s ease',
              }}
            />
          </div>

          {/* Pastilles clients cliquables */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {todayClients.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  faroLogger.info('home_today_client_clicked', {
                    clientId: c.id,
                    done: c.done,
                  });
                  navigate(`/client/${c.id}`);
                }}
                style={{
                  appearance: 'none',
                  background: c.done ? 'rgba(16,185,129,0.10)' : 'rgba(230,57,70,0.10)',
                  border: `1px solid ${
                    c.done ? 'rgba(16,185,129,0.45)' : 'rgba(230,57,70,0.45)'
                  }`,
                  borderRadius: 100,
                  padding: '7px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: 600,
                  WebkitTapHighlightColor: 'transparent',
                  maxWidth: '100%',
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: c.done ? '#10B981' : '#E63946',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 220,
                    textDecoration: c.done ? 'line-through' : 'none',
                    opacity: c.done ? 0.7 : 1,
                  }}
                >
                  {c.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate('/new-intervention')}
        style={{
          background: 'var(--teal)',
          color: 'var(--navy)',
          borderRadius: 18,
          padding: '28px 22px',
          minHeight: 140,
          boxShadow: '0 14px 32px rgba(49,220,200,0.32)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 14,
          cursor: 'pointer',
          border: 'none',
          textAlign: 'left',
          width: '100%',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span
            style={{
              width: 56,
              height: 56,
              background: 'rgba(11,22,40,0.12)',
              borderRadius: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: 'var(--navy)',
            }}
          >
            <Icon name="camera" size={34} />
          </span>
          <span style={{ flexShrink: 0, opacity: 0.7, color: 'var(--navy)' }}>
            <Icon name="arrow-right" size={24} />
          </span>
        </div>
        <span
          style={{
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: '-0.025em',
            lineHeight: 1.1,
            color: 'var(--navy)',
          }}
        >
          Saisir une nouvelle intervention
        </span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'rgba(11,22,40,0.7)',
            letterSpacing: '0.01em',
          }}
        >
          Photo de plaque, n° de parc ou choix manuel
        </span>
      </button>

      {blockedEnabled && (
        <button
          type="button"
          onClick={() => navigate('/clients-bloques')}
          style={{
            background: 'rgba(230,57,70,0.10)',
            border: '1px solid rgba(230,57,70,0.45)',
            borderLeft: '4px solid #E63946',
            borderRadius: 14,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            cursor: 'pointer',
            textAlign: 'left',
            color: 'var(--text-primary)',
            fontFamily: 'inherit',
            width: '100%',
          }}
        >
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'rgba(230,57,70,0.18)',
              color: '#FF8A95',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: 19,
              fontWeight: 800,
            }}
          >
            !
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15.5,
                fontWeight: 800,
                letterSpacing: '-0.005em',
                color: '#FF8A95',
                lineHeight: 1.2,
              }}
            >
              Clients bloqués
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                lineHeight: 1.3,
                marginTop: 2,
              }}
            >
              {blockedCount > 0
                ? `${blockedCount} client${blockedCount > 1 ? 's' : ''}`
                : 'Voir la liste et les factures impayées'}
            </div>
          </span>
          <span style={{ flexShrink: 0, color: '#FF8A95' }}>
            <Icon name="arrow-right" size={18} />
          </span>
        </button>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {cards.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => onCardClick(c.key)}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 12,
              padding: '12px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
              textAlign: 'left',
              color: 'var(--text-primary)',
              fontFamily: 'inherit',
            }}
          >
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 9,
                background: 'rgba(49,220,200,0.12)',
                color: 'var(--teal)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name={c.icon} size={17} />
            </span>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '-0.005em',
                lineHeight: 1.2,
                flex: 1,
                minWidth: 0,
              }}
            >
              {c.title}
            </div>
          </button>
        ))}
      </div>

      <WhatsNewPopup open={whatsNewOpen} onClose={closeWhatsNew} />
    </div>
  );
};

export default HomePage;
